import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Импорт новых модульных компонентов
import Button from './components/Button/Button';
import Input from './components/Input/Input';
import Select from './components/Select/Select';
import Checkbox from './components/Checkbox/Checkbox';
import Radio from './components/Radio/Radio';
import Switch from './components/Switch/Switch';
import Slider from './components/Slider/Slider';
import Modal from './components/Modal/Modal';
import Tabs from './components/Tabs/Tabs';
import Panel from './components/Panel/Panel';
import Header from './components/Header/Header';
import Sidebar from './components/Sidebar/Sidebar';
import Toolbar from './components/Toolbar/Toolbar';
import Workspace from './components/Workspace/Workspace';
import InfoCard from './components/InfoCard/InfoCard';
import FeatureCard from './components/FeatureCard/FeatureCard';
import Legend from './components/Legend/Legend';
import ConfirmDialog from './components/ConfirmDialog/ConfirmDialog';
import ExportDialog from './components/ExportDialog/ExportDialog';
import ImportDialog from './components/ImportDialog/ImportDialog';
import BulkEditDialog from './components/BulkEditDialog/BulkEditDialog';
import ThemeToggle from './components/ThemeToggle/ThemeToggle';
import { AddPlotIcon, ListIcon, LogoutIcon, MapIcon, SettingsIcon, ExportIcon, ImportIcon, EditIcon, DeleteIcon, ViewIcon, LayersIcon } from './components/Icon';

// Импорт базовых стилей
import './styles/base.css';

// Импорт страниц
import PublicMapPage from './pages/PublicMapPage';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminDocumentationPage from './pages/AdminDocumentationPage';
import IconShowcase from './components/IconShowcase';

const HomePage = () => {
    const [showModal, setShowModal] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [showExport, setShowExport] = useState(false);
    const [showImport, setShowImport] = useState(false);
    const [showBulkEdit, setShowBulkEdit] = useState(false);
    const [activeTab, setActiveTab] = useState('tab1');
    const [selectedCity, setSelectedCity] = useState('');
    const [checkedItems, setCheckedItems] = useState({});
    const [sliderValue, setSliderValue] = useState(50);
    const [switchValue, setSwitchValue] = useState(false);
    const [radioValue, setRadioValue] = useState('option1');
    const [selectedFeature, setSelectedFeature] = useState(null);

    const tabs = [
        { id: 'tab1', label: 'Вкладка 1' },
        { id: 'tab2', label: 'Вкладка 2' },
    ];

    const cityOptions = [
        { value: 'moscow', label: 'Москва' },
        { value: 'spb', label: 'Санкт-Петербург' },
        { value: 'ekb', label: 'Екатеринбург' },
        { value: 'kazan', label: 'Казань' },
    ];

    const dummyPlotData = [
        { id: 'plot_commercial', name: 'Коммерческие', color: '#ffc107' },
        { id: 'plot_residential', name: 'Жилые', color: '#28a745' },
    ];

    const dummyBuildingData = [
        { id: 'building_office', name: 'Офисные', color: '#007bff' },
        { id: 'building_retail', name: 'Торговые', color: '#dc3545' },
    ];

    const handleCheckboxChange = (id, checked) => {
        setCheckedItems(prev => ({
            ...prev,
            [id]: checked
        }));
        toast.info(`Элемент ${id}: ${checked ? 'выбран' : 'снят'}`);
    };

    // Навигация для Sidebar
    const navItems = [
        { id: 'map', label: 'Карта', icon: <MapIcon size={20} /> },
        { id: 'plots', label: 'Участки', icon: <AddPlotIcon size={20} />, badge: '12' },
        { id: 'buildings', label: 'Здания', icon: <ViewIcon size={20} />, badge: '8' },
        { id: 'roads', label: 'Дороги', icon: <MapIcon size={20} /> },
        { id: 'markers', label: 'Метки', icon: <ViewIcon size={20} /> },
        { id: 'icons', label: 'Иконки', icon: <LayersIcon size={20} /> },
        { id: 'settings', label: 'Настройки', icon: <SettingsIcon size={20} /> },
    ];

    // Действия для Header
    const headerActions = [
        { 
          id: 'theme-toggle',
          component: <ThemeToggle /> 
        },
        {
          id: 'icons',
          component: (
            <a href="/icons" style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px',
              padding: '6px 12px',
              borderRadius: 'var(--border-radius-sm)',
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--text-primary)',
              textDecoration: 'none',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              transition: 'background-color 0.2s'
            }}>
              <LayersIcon size={16} />
              Иконки
            </a>
          )
        }
    ];

    // Инструменты для Toolbar
    const tools = [
        {
            label: 'Выбор',
            items: [
                { id: 'select', label: 'Выбрать', icon: <AddPlotIcon size={18} />, title: 'Инструмент выбора (S)' },
                { id: 'pan', label: 'Панорамирование', icon: <MapIcon size={18} />, title: 'Панорамирование (P)' },
            ]
        },
        {
            label: 'Рисование',
            items: [
                { id: 'plot', label: 'Участок', icon: <AddPlotIcon size={18} />, title: 'Нарисовать участок (P)' },
                { id: 'building', label: 'Здание', icon: <ViewIcon size={18} />, title: 'Нарисовать здание (B)' },
                { id: 'road', label: 'Дорога', icon: <MapIcon size={18} />, title: 'Нарисовать дорогу (R)' },
                { id: 'marker', label: 'Метка', icon: <ViewIcon size={18} />, title: 'Добавить метку (M)' },
            ]
        },
        {
            label: 'Редактирование',
            items: [
                { id: 'edit', label: 'Редактировать', icon: <EditIcon size={18} />, title: 'Редактировать объект (E)' },
                { id: 'delete', label: 'Удалить', icon: <DeleteIcon size={18} />, title: 'Удалить объект (D)' },
                { id: 'duplicate', label: 'Дублировать', icon: <ListIcon size={18} />, title: 'Дублировать (Ctrl+D)' },
            ]
        },
    ];

    // Данные для карточек
    const features = [
        {
            id: '1',
            title: 'Коммерческий участок',
            type: 'plot',
            status: 'active',
            area: 1500,
            coordinates: { lat: 56.8416, lng: 60.6019 },
            attributes: { category: 'торговля', floors: 3 },
            image: 'https://via.placeholder.com/300x200/ffc107/ffffff?text=Plot+1'
        },
        {
            id: '2',
            title: 'Офисное здание',
            type: 'building',
            status: 'is_building',
            area: 5000,
            coordinates: { lat: 56.8420, lng: 60.6025 },
            attributes: { floors: 10, year: 2020 },
            image: 'https://via.placeholder.com/300x200/007bff/ffffff?text=Building+1'
        },
        {
            id: '3',
            title: 'Жилой комплекс',
            type: 'building',
            status: 'is_planned',
            area: 8000,
            coordinates: { lat: 56.8410, lng: 60.6010 },
            attributes: { floors: 16, apartments: 120 },
            image: 'https://via.placeholder.com/300x200/28a745/ffffff?text=Building+2'
        },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
            {/* Header */}
            <Header
                title="IChis SPD"
                subtitle="v1.2.0"
                navItems={navItems.slice(0, 4)}
                actions={headerActions}
                user={{ name: 'Администратор', avatar: null }}
                sticky
            />

            <div style={{ display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }}> {/* Added position: relative here for overall layout context */}
                {/* Sidebar */}
                <Sidebar
                    title="Инструменты"
                    sections={[
                        {
                            title: 'Слои',
                            // Здесь можно добавить логику фильтрации для легенды, если она будет частью секций сайдбара
                            // Например, если LegendSection будет отдельным компонентом, который можно встроить
                            items: [
                                { id: 'plots', label: 'Участки', icon: <AddPlotIcon size={16} />, badge: '12', active: true },
                                { id: 'buildings', label: 'Здания', icon: <ViewIcon size={16} />, badge: '8' },
                                { id: 'roads', label: 'Дороги', icon: <MapIcon size={16} /> },
                                { id: 'markers', label: 'Метки', icon: <ViewIcon size={16} /> },
                            ]
                        },
                        {
                            title: 'Действия',
                            items: [
                                { id: 'export', label: 'Экспорт', icon: <ExportIcon size={16} />, onClick: () => setShowExport(true) },
                                { id: 'import', label: 'Импорт', icon: <ImportIcon size={16} />, onClick: () => setShowImport(true) },
                                { id: 'bulk-edit', label: 'Массовое редактирование', icon: <EditIcon size={16} />, onClick: () => setShowBulkEdit(true) },
                            ]
                        }
                    ]}
                    defaultCollapsed={false}
                />

                {/* Main Content */}
                <Workspace padding="md" background="default">
                    {/* Toolbar */}
                    <Toolbar
                        tools={tools}
                        activeTool="select"
                        onToolChange={(tool) => toast.info(`Инструмент: ${tool}`)}
                        floating
                    />

                    {/* Легенда в панели */}
                    <Panel title="Легенда" style={{ marginTop: 'var(--spacing-lg)', width: '350px', height: 'calc(100vh - 200px)' }}>
                        <Legend
                            plotLegendData={dummyPlotData}
                            buildingLegendData={dummyBuildingData}
                            activePlotFilters={dummyPlotData.map(p => p.id)}
                            activeBuildingFilters={dummyBuildingData.map(b => b.id)}
                            onToggleCategory={(id, cat) => toast.info(`Toggle ${cat}: ${id}`)}
                            onSelectAll={(cat) => toast.info(`Select all ${cat}`)}
                            onClearAll={(cat) => toast.info(`Clear all ${cat}`)}
                            activeStatusFilters={['for_sale', 'is_building']}
                            onToggleStatusFilter={(status) => toast.info(`Toggle status: ${status}`)}
                            showStatusIcons={true}
                            onToggleShowStatusIcons={() => toast.info('Toggle status icons')}
                            roadLegendData={[{id: 'road_main', name: 'Основные дороги', color: '#343a40'}]}
                            activeRoadFilters={['road_main']}
                            activeRoadSubtypeFilters={['regular']}
                            onToggleSubtype={(subtype) => toast.info(`Toggle subtype: ${subtype}`)}
                            markerLegendData={[{id: 'marker_info', name: 'Инфо', image_url: 'https://via.placeholder.com/16/007bff/ffffff?text=i'}]}
                            activeMarkerFilters={['marker_info']}
                        />
                    </Panel>


                    {/* Демо компонентов */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: 'var(--spacing-lg)', marginTop: 'var(--spacing-lg)' }}>
                        {/* InfoCard */}
                        <InfoCard
                            title="Информационная карточка"
                            subtitle="Пример использования InfoCard"
                            icon={<ViewIcon size={32} />}
                            actions={[
                                { label: 'Подробнее', variant: 'primary', icon: <ViewIcon size={16} /> },
                                { label: 'Редактировать', icon: <EditIcon size={16} /> }
                            ]}
                        >
                            <p>Это пример информационной карточки с иконкой, заголовком и действиями.</p>
                        </InfoCard>

                        {/* FeatureCard */}
                        {features.map(feature => (
                            <FeatureCard
                                key={feature.id}
                                id={feature.id}
                                title={feature.title}
                                type={feature.type}
                                status={feature.status}
                                image={feature.image}
                                area={feature.area}
                                coordinates={feature.coordinates}
                                attributes={feature.attributes}
                                actions={[
                                    { label: 'Просмотр', variant: 'primary', icon: <ViewIcon size={16} />, onClick: () => toast.info(`Просмотр ${feature.title}`) },
                                    { label: 'Редактировать', icon: <EditIcon size={16} />, onClick: () => toast.info(`Редактировать ${feature.title}`) },
                                    { label: 'Удалить', variant: 'danger', icon: <DeleteIcon size={16} />, onClick: () => setShowConfirm(true) }
                                ]}
                                selected={selectedFeature === feature.id}
                                onSelect={() => setSelectedFeature(selectedFeature === feature.id ? null : feature.id)}
                            />
                        ))}
                    </div>

                    {/* Форма с компонентами */}
                    <Panel title="Демо компонентов формы" style={{ marginTop: 'var(--spacing-lg)' }}
                        footer={
                            <Button variant="primary" onClick={() => toast.success('Форма отправлена!')}>
                                Сохранить
                            </Button>
                        }>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 'var(--spacing-lg)' }}>
                            {/* Select */}
                            <div>
                                <label className="input-label">Select</label>
                                <Select
                                    label="Выберите город"
                                    options={cityOptions}
                                    value={selectedCity}
                                    onChange={setSelectedCity}
                                    placeholder="Выберите город..."
                                />
                            </div>

                            {/* Radio */}
                            <div>
                                <label className="input-label">Radio buttons</label>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
                                    <Radio
                                        id="radio-1"
                                        name="demo-radio"
                                        label="Опция 1"
                                        checked={radioValue === 'option1'}
                                        onChange={(checked) => setRadioValue(checked ? 'option1' : '')}
                                    />
                                    <Radio
                                        id="radio-2"
                                        name="demo-radio"
                                        label="Опция 2"
                                        checked={radioValue === 'option2'}
                                        onChange={(checked) => setRadioValue(checked ? 'option2' : '')}
                                    />
                                    <Radio
                                        id="radio-3"
                                        name="demo-radio"
                                        label="Опция 3 (отключена)"
                                        checked={false}
                                        disabled
                                    />
                                </div>
                            </div>

                            {/* Switch */}
                            <div>
                                <label className="input-label">Switch</label>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
                                    <Switch
                                        id="switch-1"
                                        label="Включить уведомления"
                                        checked={switchValue}
                                        onChange={setSwitchValue}
                                    />
                                    <Switch
                                        id="switch-2"
                                        label="Отключенный переключатель"
                                        checked={false}
                                        disabled
                                    />
                                </div>
                            </div>

                            {/* Slider */}
                            <div>
                                <label className="input-label">Slider: {sliderValue}%</label>
                                <Slider
                                    id="demo-slider"
                                    min={0}
                                    max={100}
                                    step={5}
                                    value={sliderValue}
                                    onChange={setSliderValue}
                                    label="Уровень"
                                />
                            </div>
                        </div>
                    </Panel>
                </Workspace>
            </div>

            {/* Модальные диалоги */}
            <ConfirmDialog
                show={showConfirm}
                onClose={() => setShowConfirm(false)}
                onConfirm={() => {
                    toast.success('Объект удален');
                    setShowConfirm(false);
                }}
                title="Удалить объект?"
                message="Вы уверены, что хотите удалить этот объект? Это действие нельзя отменить."
                destructive
            />

            <ExportDialog
                show={showExport}
                onClose={() => setShowExport(false)}
                onExport={(config) => {
                    toast.success(`Экспорт в ${config.format} (${config.layers.length} слоев)`);
                    setShowExport(false);
                }}
            />

            <ImportDialog
                show={showImport}
                onClose={() => setShowImport(false)}
                onImport={(data) => {
                    toast.success(`Импорт файла ${data.file.name} в слой ${data.targetLayer}`);
                    setShowImport(false);
                }}
            />

            <BulkEditDialog
                show={showBulkEdit}
                onClose={() => setShowBulkEdit(false)}
                onApply={(changes) => {
                    toast.success(`Изменения применены к ${Object.keys(changes).length} полям`);
                    setShowBulkEdit(false);
                }}
                selectedCount={5}
            />
        </div>
    );
};

const App = () => {
    return (
        <Router
            // Для устранения предупреждений React Router Future Flag
            // Эти флаги включают поведение, которое станет стандартным в v7
            future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
            }}
        >
            <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/icons" element={<IconShowcase />} />
                <Route path="/map/" element={<PublicMapPage />} />
                <Route path="/map/admin" element={<AdminLoginPage />} />
                <Route path="/map/admin/dashboard" element={<AdminDashboardPage />} />
                <Route path="/map/admin/documentation" element={<AdminDocumentationPage />} />
                {/* Добавьте другие маршруты по мере необходимости */}
            </Routes>
            <ToastContainer position="bottom-right" autoClose={3000} hideProgressBar={false} />
        </Router>
    );
};

export default App;